import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import axios from "axios";

const SDK_ID = "meta-facebook-jssdk";

const WhatsAppEmbeddedSignupPanel = ({ connectionId, config = {}, onConnectionChanged }) => {
    const [metaConfig, setMetaConfig] = useState(null);
    const [pipelines, setPipelines] = useState([]);
    const [pipelineId, setPipelineId] = useState("");
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState(null);
    const sessionRef = useRef(null);
    const connected = Boolean(config.oauth_connected);

    useEffect(() => {
        Promise.all([
            axios.get("/api/connections/whatsapp/embedded-signup/config"),
            axios.get("/api/pipelines"),
        ]).then(([signup, pipelineResponse]) => {
            setMetaConfig(signup.data);
            const items = pipelineResponse.data?.pipelines || [];
            setPipelines(items);
            const current = items.find(item => Number(item.id) === Number(config.pipeline_id));
            const fallback = current || items.find(item => item.is_default) || items[0];
            if (fallback) setPipelineId(String(fallback.id));
        }).catch(error => setMessage({
            type: "danger",
            text: error.response?.data?.message || "O Embedded Signup ainda não foi configurado.",
        }));
    }, [config.pipeline_id]);

    useEffect(() => {
        const receiveSession = event => {
            if (!String(event.origin).includes("facebook.com")) return;
            let payload = event.data;
            try { if (typeof payload === "string") payload = JSON.parse(payload); } catch (_) { return; }
            if (payload?.type !== "WA_EMBEDDED_SIGNUP") return;
            if (payload.event === "FINISH") {
                sessionRef.current = {
                    waba_id: payload.data?.waba_id,
                    phone_number_id: payload.data?.phone_number_id,
                };
            } else if (payload.event === "CANCEL" || payload.event === "ERROR") {
                setBusy(false);
                setMessage({ type: "warning", text: payload.data?.error_message || "A conexão foi cancelada na Meta." });
            }
        };
        window.addEventListener("message", receiveSession);
        return () => window.removeEventListener("message", receiveSession);
    }, []);

    useEffect(() => {
        if (!metaConfig?.app_id) return;
        window.fbAsyncInit = () => window.FB.init({
            appId: metaConfig.app_id,
            autoLogAppEvents: true,
            xfbml: false,
            version: metaConfig.graph_version,
        });
        if (window.FB) window.fbAsyncInit();
        else if (!document.getElementById(SDK_ID)) {
            const script = document.createElement("script");
            script.id = SDK_ID;
            script.async = true;
            script.defer = true;
            script.crossOrigin = "anonymous";
            script.src = "https://connect.facebook.net/pt_BR/sdk.js";
            document.body.appendChild(script);
        }
    }, [metaConfig]);

    const waitForSession = () => new Promise((resolve, reject) => {
        const started = Date.now();
        const timer = window.setInterval(() => {
            if (sessionRef.current?.waba_id && sessionRef.current?.phone_number_id) {
                window.clearInterval(timer);
                resolve(sessionRef.current);
            } else if (Date.now() - started > 15000) {
                window.clearInterval(timer);
                reject(new Error("A Meta não retornou os dados da conta e do número."));
            }
        }, 150);
    });

    const connect = () => {
        setBusy(true);
        setMessage(null);
        sessionRef.current = null;
        if (!window.FB || !metaConfig?.config_id) {
            setBusy(false);
            setMessage({ type: "danger", text: "O SDK da Meta ainda não está disponível. Tente novamente." });
            return;
        }
        window.FB.login(response => {
            void (async () => {
            try {
                const code = response?.authResponse?.code;
                if (!code) throw new Error("A Meta não retornou o código de autorização.");
                const session = await waitForSession();
                const { data } = await axios.post("/api/connections/whatsapp/embedded-signup/complete", {
                    code,
                    waba_id: session.waba_id,
                    phone_number_id: session.phone_number_id,
                    pipeline_id: pipelineId ? Number(pipelineId) : null,
                    connection_id: connectionId || null,
                });
                setMessage({ type: "success", text: data.message });
                await onConnectionChanged?.(data.connection?.id);
            } catch (error) {
                setMessage({ type: "danger", text: error.response?.data?.message || error.message });
            } finally {
                setBusy(false);
            }
            })();
        }, {
            config_id: metaConfig.config_id,
            response_type: "code",
            override_default_response_type: true,
            extras: { setup: {} },
        });
    };

    return (
        <div className="mb-4 rounded border p-3" style={{ background: "#ecfdf5", borderColor: "#86efac" }}>
            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                <div>
                    <strong>{connected ? "WhatsApp Oficial conectado" : "Conecte o WhatsApp da empresa"}</strong>
                    <div className="small text-muted mt-1">
                        A clínica entra diretamente na Meta e mantém a propriedade do número, da conta e da cobrança.
                    </div>
                </div>
                <span className={`badge ${connected ? "bg-success" : "bg-secondary"}`}>
                    {connected ? "Conectado" : "Não conectado"}
                </span>
            </div>
            {message && <Alert variant={message.type} className="py-2 small">{message.text}</Alert>}
            <Form.Group className="mb-3">
                <Form.Label>Pipeline para novos contatos</Form.Label>
                <Form.Select value={pipelineId} onChange={event => setPipelineId(event.target.value)}>
                    <option value="">Selecione...</option>
                    {pipelines.map(pipeline => <option key={pipeline.id} value={pipeline.id}>{pipeline.nome}</option>)}
                </Form.Select>
            </Form.Group>
            <Button type="button" variant="success" disabled={busy || !pipelineId || !metaConfig} onClick={connect}>
                {busy && <Spinner size="sm" className="me-2" />}
                {connected ? "Reconectar com a Meta" : "Conectar com a Meta"}
            </Button>
        </div>
    );
};

export default WhatsAppEmbeddedSignupPanel;
